import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DossierMember } from '../../models/DossierMember';
import { DossierGroup } from '../../models/DossierGroup';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../app/store';
import Form from 'react-bootstrap/esm/Form';
import { ConfirmModal } from '../../features/confirm-modal/ConfirmModal';
import PersonSelector from '../appointments/PersonSelector';
import Alert from 'react-bootstrap/esm/Alert';
import { UserRelationRoleEnum } from '../../api/types';
import { ISessionAppState } from '../../app/session/reducer';
import { Api } from '../../api';
import { NotificationHelper } from '../../helpers/NotificationHelper';

export interface INewGroupFormProps {
  onCreated: (group: DossierGroup) => void;
  onCancel: () => void;
}

export function NewGroupForm({ onCreated, onCancel }: INewGroupFormProps): JSX.Element {
  const session = useSelector<ApplicationState, ISessionAppState>(state => state.session);
  const dossierRelations = useMemo<DossierMember[]>(() => (session.dossierRelations || []).map(relation => new DossierMember(relation)), [session.dossierRelations]);
  const [members, setMembers] = useState<DossierMember[]>([]);
  const [membersError, setMembersError] = useState<string|undefined >(undefined);
  const [groupName, setGroupName] = useState<string>('');
  const [groupNameError, setGroupNameError] = useState<string|undefined >(undefined);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const isValid = useMemo<boolean>(() => !groupNameError && !membersError, [groupNameError, membersError]);

  const validate = useCallback((): boolean => {
    setMembersError(undefined);
    setGroupNameError(undefined);

    let isValid = true;
    if (!groupName || !groupName.trim()) {
      setGroupNameError('Vul een naam in');
      isValid = false;
    } else if (groupName.trim().length > 255) {
      setGroupNameError('Naam mag niet langer zijn dan 255 tekens');
      isValid = false;
    }

    if (members.length < 2) {
      setMembersError('Selecteer minimaal twee personen');
      isValid = false;
    } else if (members.some(member => member.role === UserRelationRoleEnum.caregiver) === false) {
      setMembersError('Iedere groep moet minimaal één behandelaar hebben');
      isValid = false;
    }

    return isValid;
  }, [groupName, members]);

  useEffect(() => {
    if (!isValid) {
      validate();
    }
  }, [members, groupName, isValid, validate]);

  const clear = (): void => {
    setMembers([]);
    setMembersError(undefined);
    setGroupName('');
    setGroupNameError(undefined);
  };

  const handleCancel = (): void => {
    clear();
    onCancel();
  };

  const handleSubmit = (): void => {
    if (!validate() || !session.dossier) {
      return;
    }
    setSubmitting(true);

    Api.createChatGroup(session.dossier.id, groupName.trim(), members)
      .then(group => {
        clear();
        onCreated(group);
      })
      .catch(err => {
        console.error(err);
        NotificationHelper.toastError('Aanmaken groep mislukt door een onverwachte fout');
      })
      .finally(() => setSubmitting(false));
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    handleSubmit();
  };

  const handleSelectMember = (selectedMember: DossierMember): void => {
    const index = members.findIndex(member => member.memberId === selectedMember.memberId);
    if (index === -1) {
      setMembers([...members, selectedMember]);
    } else {
      const copy = [...members];
      copy.splice(index, 1);
      setMembers(copy);
    }
  };

  const renderBody = () => <>
    <Form onSubmit={handleFormSubmit}>
      <Form.Group className="form__item new-group-form__name">
        <Form.Control
          name="group_input_name"
          type="text"
          autoComplete="off"
          placeholder="Groepnaam"
          onChange={e => setGroupName(e.target.value.trimStart())}
          value={groupName}
          isInvalid={!!groupNameError}
        />
        { groupNameError && <Alert className="new-group-form__danger" variant="danger">{groupNameError}</Alert> }
      </Form.Group>
    </Form>

    <PersonSelector persons={dossierRelations} selected={members} onClickItem={handleSelectMember} keepDropdownOpenOnItemClick={true} />
    { membersError && <Alert className="new-group-form__danger" variant="danger">{membersError}</Alert> }
  </>;

  return <ConfirmModal
    title="Groep aanmaken"
    body={renderBody()}
    confirmLabel="Aanmaken"
    confirmButtonStyle="default"
    onCancel={handleCancel}
    onConfirm={handleSubmit}
    confirming={submitting}
  />;
}
